import React, { useState, useEffect, useMemo, useRef } from "react";
import { useContacts } from "../context/ContactsContext";
import { useTags } from "../context/TagsContext";
import { updateContact, fetchCustomerTypeOptions, fetchContactMethodOptions, fetchOwnerOptions, deleteContact } from "../api/contacts";
import { createEvent, updateEvent } from "../api/eventsApi";
import { createContactLink, updateContactLink, deleteContactLink } from "../api/links";
import { useAutoSave } from "../hooks/useAutoSave";
import "./ContactInfo.css";
import Calendar from "./Calendar";
import EditModal from "./Edit-Modal";
import TagModal from "./TagModal";
import LinkModal from "./LinkModal";

export default function ContactInfo({ contactId }) {
  // Globale Tags (aus /api/v1/tags)
  const { tags: allTags = [], loading: tagsLoading } = useTags();
  // Kontakt-Details (inkl. detail.tags)
  const { getContactDetail, detailLoading, addTagToContact, removeTagFromContact, setSelectedContactId } = useContacts();

  const [detail, setDetail] = useState(null);


  const [formData, setFormData] = useState({
    name: "",
    firma: "",
    email: "",
    phone: "",
    customerType: "",
    contactMethod: "",
    customerInfo: "",
    eventDetails: "",
    contactOwner: "",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalField, setModalField] = useState("");
  const [modalItems, setModalItems] = useState([]); // [{id,label,assigned}]
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSelectedId, setModalSelectedId] = useState(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagModalQuery, setTagModalQuery] = useState("");
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [confirmContactModalOpen, setConfirmContactModalOpen] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const customerInfoRef = useRef(null);
  const eventDetailsRef = useRef(null);

  // Kontakt-Detail laden wenn ID sich ändert
  useEffect(() => {
    if (!contactId) return;

    let active = true;

    (async () => {
      try {
        const d = await getContactDetail(contactId);
        if (!active) return;

        setDetail(d);

        const eventDetails = (d.events || [])
          .map((ev) => ev.description || "")
          .filter(Boolean)
          .join(", ");

        setFormData({
          name: d.name || "",
          firma: d.company || "",
          email: d.email || "",
          phone: d.phone || "",
          customerType: d.customerTypes?.[0]?.type || "",
          contactMethod: d.contactMethods?.[0]?.method || "",
          customerInfo: d.customerInfo || "",
          eventDetails,
          contactOwner: d.owners?.[0]?.username || "",
        });
      } catch (err) {
        // Silent fail
      }
    })();

    return () => {
      active = false;
    };
  }, [contactId, getContactDetail]);

  // 🔐 Auto-Save mit Debouncing (800ms nach letzter Änderung)
  useAutoSave(
    formData,
    async (data) => {
      if (!contactId || !detail) return;

      // Nur Felder senden die nicht read-only sind
      const updatePayload = {
        name: data.name !== detail.name ? data.name : undefined,
        company: data.firma !== detail.company ? data.firma : undefined,
        email: data.email !== detail.email ? data.email : undefined,
        phone: data.phone !== detail.phone ? data.phone : undefined,
        customerInfo: data.customerInfo !== detail.customerInfo ? data.customerInfo : undefined,
      };

      // Nur Felder mit Wert senden
      const filteredPayload = Object.fromEntries(
        Object.entries(updatePayload).filter(([_, v]) => v !== undefined)
      );

      if (Object.keys(filteredPayload).length === 0) {
        console.log("ℹ️ Keine Änderungen zum Speichern");
        return;
      }

      console.log("Auto-Save:", filteredPayload);
      await updateContact(contactId, filteredPayload);
      console.log("✅ Kontakt gespeichert");
    },
    800 // 800ms Debounce
  );

  // ------------------ Tag UI ------------------
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const addTagInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const norm = (s) => (s ?? "").toString().trim().toLowerCase();

  // Logs fürs Debugging
  useEffect(() => {
    console.log("🧰 allTags (global):", Array.isArray(allTags) ? allTags.length : 0, allTags);
  }, [allTags]);
  useEffect(() => {
    console.log("🟢 isAddingTag:", isAddingTag);
  }, [isAddingTag]);

  // IDs der bereits zugewiesenen (aktiven) Tags dieses Kontakts
  const assignedIds = useMemo(() => {
    const list = (detail?.tags || []).filter((t) => t.active);
    const ids = new Set(list.map((t) => t.id));
    console.log("🏷️ assignedIds:", Array.from(ids));
    return ids;
  }, [detail]);

  // Clientseitige Suche in allen Tags, aber ohne bereits zugewiesene
  const suggestions = useMemo(() => {
    const q = norm(newTag);
    const base = (Array.isArray(allTags) ? allTags : []).filter((t) => !assignedIds.has(t.id));
    const res =
      q
        ? base.filter(
            (t) =>
              norm(t.label).includes(q) ||
              norm(t.description).includes(q) ||
              norm(t.name).includes(q)
          )
        : base; // oder [] falls du erst nach Eingabe anzeigen willst
    console.log("🔎 suggestions for", newTag, "=>", res.map((t) => ({ id: t.id, label: t.label, name: t.name })));
    return res;
  }, [allTags, newTag, assignedIds]);

  const exactMatch = useMemo(() => {
    const q = norm(newTag);
    if (!q) return null;
    const m = suggestions.find((t) => norm(t.label) === q || norm(t.name) === q) || null;
    console.log("🎯 exactMatch:", m);
    return m;
  }, [suggestions, newTag]);

  useEffect(() => {
    if (isAddingTag && addTagInputRef.current) {
      addTagInputRef.current.focus();
    }
  }, [isAddingTag]);

  useEffect(() => {
    if (!isAddingTag) return;
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (dropdownRef.current.contains(e.target) || addTagInputRef.current?.contains(e.target)) return;
      setIsAddingTag(false);
      setHighlightIndex(-1);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isAddingTag]);

  const selectTag = async (tag, source = "click") => {
    if (!tag || !detail?.id) return;
    console.log(`✅ Tag selected (${source}):`, { id: tag.id, label: tag.label, name: tag.name });

    try {
      // optional: optimistic UI
      setDetail(prev => prev ? { ...prev, tags: [...(prev.tags || []), { id: tag.id, label: tag.label || tag.name, type: tag.type || "Basic", active: true }] } : prev);

      await addTagToContact(detail.id, tag.id);

      // sauber nachziehen (falls du nicht rein optimistisch bleiben willst)
      const updated = await getContactDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      console.error("❌ Fehler beim Anhängen des Tags:", err);
    } finally {
      setNewTag("");
      setIsAddingTag(false);
      setHighlightIndex(-1);
    }
  };

  const handleRemoveTag = async (tagId) => {
    if (!tagId || !detail?.id) return;
    try {
      await removeTagFromContact(detail.id, tagId);
      const updated = await getContactDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      console.error("❌ Fehler beim Entfernen des Tags:", err);
    }
  };

  const handleAddTag = () => {
    const q = newTag.trim();
    if (!q) return;

    if (exactMatch) {
      selectTag(exactMatch, "enter-exact");
    } else {
      console.log(`ℹ️ Kein exakter Tag – öffne TagModal für freien Text: "${q}"`);
      setTagModalQuery(q);
      setTagModalOpen(true);
      setIsAddingTag(false);
      setHighlightIndex(-1);
    }
  };

  const handleKeyDown = (e) => {
    if (!isAddingTag) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!suggestions.length) return;
      setHighlightIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!suggestions.length) return;
      setHighlightIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && suggestions[highlightIndex]) {
        selectTag(suggestions[highlightIndex], "enter-highlight");
      } else {
        handleAddTag();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsAddingTag(false);
      setHighlightIndex(-1);
    }
  };

  // Tags aus Detail übernehmen (Anzeige-Chips)
  const { basicTags, checkmarkTags } = useMemo(() => {
    if (!detail) return { basicTags: [], checkmarkTags: [] };
    const enriched = (detail.tags || []).map((t) => ({ ...t, checked: t.active }));
    return {
      basicTags: enriched.filter((t) => t.type === "basic"),
      checkmarkTags: enriched.filter((t) => t.type === "checkmark"),
    };
  }, [detail]);

  const handleChange = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const resizeTextArea = (ref) => {
    if (!ref?.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = `${ref.current.scrollHeight}px`;
  };

  const handleTextAreaChange = (field, ref) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    resizeTextArea(ref);
  };

  useEffect(() => {
    resizeTextArea(customerInfoRef);
    resizeTextArea(eventDetailsRef);
  }, [formData.customerInfo, formData.eventDetails]);

  // Kalendertag-Klick Handler: Event erstellen oder verschieben
  const handleCalendarDateSelect = async (selectedDate) => {
    if (!detail?.id) return;

    // Lokales Datum ohne Timezone-Probleme formatieren
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD

    // Prüfe ob bereits ein Event existiert
    const existingEvent = detail.events?.[0]; // Nimm das erste Event

    try {
      if (existingEvent) {
        // Event verschieben
        console.log("📅 Verschiebe Event", existingEvent.id, "zu", dateStr);
        await updateEvent(existingEvent.id, { eventDate: dateStr });
      } else {
        // Neues Event anlegen
        console.log("📅 Erstelle neues Event für Kontakt", detail.id, "am", dateStr);
        await createEvent({
          contactId: detail.id,
          title: "Event",
          description: "",
          eventDate: dateStr,
        });
      }

      // Detail neu laden um aktualisierte Events zu bekommen
      const updated = await getContactDetail(detail.id);
      setDetail(updated);
    } catch (err) {
      console.error("❌ Fehler beim Event erstellen/verschieben:", err);
    }
  };

  // Modal öffnen und Optionen laden
  const openEditModal = async (field) => {
    if (!detail?.id) return;
    setModalField(field);
    setModalOpen(true);
    setModalLoading(true);
    setModalItems([]);
    setModalSelectedId(null);
    try {
      let resp;
      if (field === "customerType") {
        resp = await fetchCustomerTypeOptions(detail.id);
      } else if (field === "contactMethod") {
        resp = await fetchContactMethodOptions(detail.id);
      } else if (field === "contactOwner") {
        resp = await fetchOwnerOptions(detail.id);
      }
      const items = resp?.items || [];
      setModalItems(items);
      const assigned = items.find((i) => i.assigned);
      setModalSelectedId(assigned ? assigned.id : null);
    } catch (e) {
      console.error("❌ Fehler beim Laden der Optionen:", e);
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalSave = async () => {
    if (!detail?.id || !modalField || modalSelectedId == null) return;
    try {
      if (modalField === "customerType") {
        await updateContact(detail.id, { customerTypeIdChange: modalSelectedId });
      } else if (modalField === "contactMethod") {
        await updateContact(detail.id, { contactMethodIdChange: modalSelectedId });
      } else if (modalField === "contactOwner") {
        await updateContact(detail.id, { ownerIdChange: modalSelectedId });
      }
      const updated = await getContactDetail(detail.id);
      setDetail(updated);
      setFormData((prev) => ({
        ...prev,
        customerType: updated.customerTypes?.[0]?.type || prev.customerType,
        contactMethod: updated.contactMethods?.[0]?.method || prev.contactMethod,
        contactOwner: updated.owners?.[0]?.username || prev.contactOwner,
      }));
      setModalOpen(false);
    } catch (e) {
      console.error("❌ Fehler beim Speichern der Auswahl:", e);
    }
  };

  // Link handlers
  const handleLinkSave = async (linkData) => {
    if (!detail?.id) return;
    try {
      if (editingLink) {
        await updateContactLink(detail.id, editingLink.id, linkData);
      } else {
        await createContactLink(detail.id, linkData);
      }
      const updated = await getContactDetail(detail.id);
      setDetail(updated);
      setLinkModalOpen(false);
      setEditingLink(null);
    } catch (e) {
      console.error("❌ Fehler beim Speichern des Links:", e);
    }
  };

  const handleLinkDelete = async (linkId) => {
    if (!detail?.id) return;
    try {
      await deleteContactLink(detail.id, linkId);
      const updated = await getContactDetail(detail.id);
      setDetail(updated);
      setLinkModalOpen(false);
      setEditingLink(null);
    } catch (e) {
      console.error("❌ Fehler beim Löschen des Links:", e);
    }
  };

  // ------------------ Render ------------------
  if (detailLoading && !detail) {
    return <p>⏳ Lade Kontakt...</p>;
  }

  if (!contactId) {
    return <p>⚠️ Keine Kontakt-ID übergeben</p>;
  }

  if (!detail) {
    return <p>🔍 Lade Daten für Kontakt {contactId} ...</p>;
  }

  return (
    <div className={`contact-info-container ${detail?.canContact === false ? "cannot-contact" : ""}`}>
      <div className="contact-header">
        <div className="contact-name">
          <label>Name</label>
          <input type="text" value={formData.name} onChange={handleChange("name")} />
        </div>
        <div className="contact-company">
          <label>Firma</label>
          <input type="text" value={formData.firma} onChange={handleChange("firma")} />
        </div>
      </div>

      <div className="contact-tags">
        {basicTags.map((t) => (
          <span key={t.id} className="tag tag-removable">
            {t.label || t.name}
            <button
              type="button"
              className="tag-remove"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(t.id);
              }}
              title="Tag entfernen"
            >
              ✕
            </button>
          </span>
        ))}
        {checkmarkTags.map((t) => (
          <span key={t.id} className="tag tag-removable">
            {t.label || t.name} ✔️
            <button
              type="button"
              className="tag-remove"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(t.id);
              }}
              title="Tag entfernen"
            >
              ✕
            </button>
          </span>
        ))}
        {!isAddingTag ? (
          <span
            className="tag add-tag"
            onClick={() => {
              setIsAddingTag(true);
              setHighlightIndex(-1);
            }}
          >
            +
          </span>
        ) : (
          <div className="add-tag-combo">
            <div className="add-tag-input-wrapper">
              <input
                ref={addTagInputRef}
                className="tag-input"
                type="text"
                value={newTag}
                placeholder={tagsLoading ? "Tags laden…" : "Tag suchen oder eingeben…"}
                onChange={(e) => {
                  setNewTag(e.target.value);
                  setHighlightIndex(0);
                  console.log("⌨️ onChange ->", e.target.value);
                }}
                onKeyDown={handleKeyDown}
              />
              <button
                type="button"
                className="add-tag-confirm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleAddTag}
                title="Hinzufügen / Auswählen"
              >
                +
              </button>
            </div>
            <div className="tag-suggestions" ref={dropdownRef}>
              {suggestions.map((t, idx) => {
                const isActive = idx === highlightIndex;
                return (
                  <div
                    key={t.id}
                    className={`tag-suggestion${isActive ? " active" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectTag(t, "click");
                    }}
                  >
                    <span className="tag-suggestion-label">{t.label || t.name}</span>
                    {t.description && (
                      <span className="tag-suggestion-desc">{t.description}</span>
                    )}
                  </div>
                );
              })}
              {!suggestions.length && newTag.trim() && (
                <>
                  <div className="tag-suggestion empty">Keine Treffer</div>
                  {console.log("❌ Keine Treffer für:", newTag)}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="contact-body">
        <div className="contact-left">
          <label>Email</label>
          <input type="email" value={formData.email} onChange={handleChange("email")} />

          <label>Telefon</label>
          <input type="tel" value={formData.phone} onChange={handleChange("phone")} />

          <label>Customer Type</label>
          <div className="readonly-field">
            <span>{formData.customerType}</span>
            <div
              className="edit-icon"
              onClick={() => openEditModal("customerType")}
            >
              <span className="divider" />
              <span className="icon">✏️</span>
            </div>
          </div>

          <label>Contact Method</label>
          <div className="readonly-field">
            <span>{formData.contactMethod}</span>
            <div
              className="edit-icon"
              onClick={() => openEditModal("contactMethod")}
            >
              <span className="divider" />
              <span className="icon">✏️</span>
            </div>
          </div>

          <label>Contact Owner</label>
          <div className="readonly-field">
            <span>{formData.contactOwner}</span>
            <div
              className="edit-icon"
              onClick={() => openEditModal("contactOwner")}
            >
              <span className="divider" />
              <span className="icon">✏️</span>
            </div>
          </div>
        </div>

        <div className="contact-right">
          <label>Kalender</label>
          <Calendar 
            key={detail?.events?.map(e => e.id).join(',') || 'no-events'}
            selectedContact={detail}
            onSelect={handleCalendarDateSelect}
          />
        </div>
      </div>

      <div className="contact-links">
        {(detail?.links || []).map((l) => (
          <span
            key={l.id}
            className="link-tag"
            onClick={() => window.open(l.link, "_blank")}
            onMouseEnter={(e) => {
              const editBtn = e.currentTarget.querySelector(".link-edit-btn");
              if (editBtn) editBtn.style.display = "inline-flex";
            }}
            onMouseLeave={(e) => {
              const editBtn = e.currentTarget.querySelector(".link-edit-btn");
              if (editBtn) editBtn.style.display = "none";
            }}
          >
            {l.name}
            <span
              className="link-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                setEditingLink(l);
                setLinkModalOpen(true);
              }}
            >
              ✏️
            </span>
          </span>
        ))}
        <span
          className="tag add-tag link-add"
          onClick={() => {
            setEditingLink(null);
            setLinkModalOpen(true);
          }}
        >
          +
        </span>
      </div>

      <div className="info-details-wrapper">
        <div className="customer-info">
          <label>Customer Info</label>
          <textarea
            ref={customerInfoRef}
            className="auto-resize-textarea"
            value={formData.customerInfo}
            onChange={handleTextAreaChange("customerInfo", customerInfoRef)}
          />
        </div>

        <div className="event-details">
          <label>Event Details</label>
          <textarea
            ref={eventDetailsRef}
            className={`auto-resize-textarea ${(!detail?.events || detail?.events.length === 0) ? "disabled-event" : ""}`}
            value={formData.eventDetails}
            onChange={handleTextAreaChange("eventDetails", eventDetailsRef)}
            disabled={!detail?.events || detail?.events.length === 0}
            placeholder={(!detail?.events || detail?.events.length === 0) ? "Hat keinen Kalendereintrag" : ""}
          />
        </div>

        <EditModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          modalField={modalField}
          items={modalItems}
          loading={modalLoading}
          onChangeSelected={setModalSelectedId}
          onSave={handleModalSave}
        />
        <TagModal
          isOpen={tagModalOpen}
          onClose={() => setTagModalOpen(false)}
          allTags={allTags}               
          initialQuery={tagModalQuery}
          onSave={(tag) => {
            console.log("🆕 Tag created/selected via modal:", tag);
            setNewTag("");
            setTagModalOpen(false);
          }}
        />
        <LinkModal
          isOpen={linkModalOpen}
          onClose={() => {
            setLinkModalOpen(false);
            setEditingLink(null);
          }}
          initialLink={editingLink}
          onSave={handleLinkSave}
          onDelete={handleLinkDelete}
        />
      </div>

      <div className="contact-info-footer">
        <button
          className="btn-delete"
          onClick={() => setConfirmDeleteModalOpen(true)}
        >
          Kontakt löschen
        </button>
        <button
          className={`toggle-contact-btn ${detail?.canContact ? "can-contact" : "cannot-contact"}`}
          onClick={() => {
            // Wenn von Do not contact zu Contact allowed wechseln -> Confirmation Modal
            if (!detail?.canContact) {
              setConfirmContactModalOpen(true);
            } else {
              // Direkter Toggle zu Do not contact
              setDetail({ ...detail, canContact: false });
              updateContact(contactId, { canContact: false });
            }
          }}
        >
          {detail?.canContact ? "Nicht Kontaktieren" : "Kontaktieren erlauben"}
        </button>
      </div>

      {/* Confirmation Modal for enabling contact */}
      {confirmContactModalOpen && (
        <div className="modal-overlay" onClick={() => setConfirmContactModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Kontakt aktivieren?</h2>
            <p>Möchtest du diesen Kontakt wieder aktivieren und darfst ihn kontaktieren?</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setConfirmContactModalOpen(false)}
              >
                Abbrechen
              </button>
              <button
                className="btn-confirm"
                onClick={() => {
                  setDetail({ ...detail, canContact: true });
                  updateContact(contactId, { canContact: true });
                  setConfirmContactModalOpen(false);
                }}
              >
                Ja, aktivieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for deleting contact */}
      {confirmDeleteModalOpen && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteModalOpen(false)}>
          <div className="modal-content modal-danger" onClick={(e) => e.stopPropagation()}>
            <h2>Kontakt löschen?</h2>
            <p>Dieser Kontakt wird permanent gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setConfirmDeleteModalOpen(false)}
              >
                Abbrechen
              </button>
              <button
                className="btn-delete-confirm"
                onClick={async () => {
                  try {
                    await deleteContact(contactId);
                    setConfirmDeleteModalOpen(false);
                    setSelectedContactId(null); // Navigate away
                  } catch (err) {
                    // Silent fail
                  }
                }}
              >
                Ja, löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
